import { CheckIcon, CopyIcon } from '@/components/icons';
import { useClipboard } from '@/hooks/useClipboard';
import { formatDateTime } from '@/utils/date';
import type { X509CertificateInfo, X509ExtKeyUsage, X509KeyUsage } from '@/types/certificate';

/**
 * Get key usage labels
 */
function getKeyUsageLabels(keyUsage?: X509KeyUsage): string[] {
  if (!keyUsage) return [];
  const labels: string[] = [];
  if (keyUsage.digital_signature) labels.push('Digital Signature');
  if (keyUsage.key_encipherment) labels.push('Key Encipherment');
  if (keyUsage.data_encipherment) labels.push('Data Encipherment');
  if (keyUsage.cert_sign) labels.push('Certificate Sign');
  if (keyUsage.crl_sign) labels.push('CRL Sign');
  return labels;
}

/**
 * Get extended key usage labels
 */
function getExtKeyUsageLabels(extKeyUsage?: X509ExtKeyUsage): string[] {
  if (!extKeyUsage) return [];
  const labels: string[] = [];
  if (extKeyUsage.server_auth) labels.push('TLS Web Server Authentication');
  if (extKeyUsage.client_auth) labels.push('TLS Web Client Authentication');
  if (extKeyUsage.code_signing) labels.push('Code Signing');
  if (extKeyUsage.email_protection) labels.push('E-mail Protection');
  if (extKeyUsage.time_stamping) labels.push('Time Stamping');
  if (extKeyUsage.ocsp_signing) labels.push('OCSP Signing');
  return labels;
}

/**
 * Certificate info section component
 */
export function CertificateInfoSection({ cert, title }: { cert: X509CertificateInfo; title?: string }) {
  const { copiedKey: copiedField, copy: handleCopy } = useClipboard();

  const keyUsageLabels = getKeyUsageLabels(cert.key_usage);
  const extKeyUsageLabels = getExtKeyUsageLabels(cert.ext_key_usage);
  const hasSANs = (cert.subject_alt_names?.dns_names?.length ?? 0) > 0 ||
                  (cert.subject_alt_names?.ip_addresses?.length ?? 0) > 0;

  return (
    <div className="cert-details-info-section">
      {title && <h4 className="cert-details-section-title">{title}</h4>}

      {/* Subject */}
      <div className="cert-details-group">
        <h5 className="cert-details-group-title">Subject</h5>
        <div className="cert-details-field">
          <span className="cert-details-label">Common Name (CN)</span>
          <span className="cert-details-value">{cert.subject.common_name || '-'}</span>
        </div>
        {cert.subject.organization?.length ? (
          <div className="cert-details-field">
            <span className="cert-details-label">Organization (O)</span>
            <span className="cert-details-value">{cert.subject.organization.join(', ')}</span>
          </div>
        ) : null}
        {cert.subject.organizational_unit?.length ? (
          <div className="cert-details-field">
            <span className="cert-details-label">Organizational Unit (OU)</span>
            <span className="cert-details-value">{cert.subject.organizational_unit.join(', ')}</span>
          </div>
        ) : null}
        {cert.subject.country?.length ? (
          <div className="cert-details-field">
            <span className="cert-details-label">Country (C)</span>
            <span className="cert-details-value">{cert.subject.country.join(', ')}</span>
          </div>
        ) : null}
        {cert.subject.province?.length ? (
          <div className="cert-details-field">
            <span className="cert-details-label">State/Province (ST)</span>
            <span className="cert-details-value">{cert.subject.province.join(', ')}</span>
          </div>
        ) : null}
        {cert.subject.locality?.length ? (
          <div className="cert-details-field">
            <span className="cert-details-label">Locality (L)</span>
            <span className="cert-details-value">{cert.subject.locality.join(', ')}</span>
          </div>
        ) : null}
      </div>

      {/* Issuer */}
      <div className="cert-details-group">
        <h5 className="cert-details-group-title">Issuer</h5>
        <div className="cert-details-field">
          <span className="cert-details-label">Common Name (CN)</span>
          <span className="cert-details-value">{cert.issuer.common_name || '-'}</span>
        </div>
        {cert.issuer.organization?.length ? (
          <div className="cert-details-field">
            <span className="cert-details-label">Organization (O)</span>
            <span className="cert-details-value">{cert.issuer.organization.join(', ')}</span>
          </div>
        ) : null}
        {cert.issuer.country?.length ? (
          <div className="cert-details-field">
            <span className="cert-details-label">Country (C)</span>
            <span className="cert-details-value">{cert.issuer.country.join(', ')}</span>
          </div>
        ) : null}
        {cert.is_self_signed && (
          <div className="cert-details-badge cert-details-badge-warning">Self-Signed</div>
        )}
      </div>

      {/* Validity */}
      <div className="cert-details-group">
        <h5 className="cert-details-group-title">Validity</h5>
        <div className="cert-details-field">
          <span className="cert-details-label">Not Before</span>
          <span className="cert-details-value">{formatDateTime(cert.not_before)}</span>
        </div>
        <div className="cert-details-field">
          <span className="cert-details-label">Not After</span>
          <span className="cert-details-value">{formatDateTime(cert.not_after)}</span>
        </div>
        <div className="cert-details-field">
          <span className="cert-details-label">Status</span>
          <span className={`cert-details-status ${cert.is_expired ? 'expired' : cert.days_remaining <= 30 ? 'warning' : 'valid'}`}>
            {cert.is_expired
              ? 'Expired'
              : cert.days_remaining <= 0
                ? 'Expiring today'
                : `${cert.days_remaining} days remaining`}
          </span>
        </div>
      </div>

      {/* Certificate Info */}
      <div className="cert-details-group">
        <h5 className="cert-details-group-title">Certificate Info</h5>
        <div className="cert-details-field">
          <span className="cert-details-label">Version</span>
          <span className="cert-details-value">V{cert.version}</span>
        </div>
        <div className="cert-details-field">
          <span className="cert-details-label">Serial Number</span>
          <span className="cert-details-value cert-details-mono">{cert.serial_number}</span>
        </div>
        <div className="cert-details-field">
          <span className="cert-details-label">Signature Algorithm</span>
          <span className="cert-details-value">{cert.signature_algorithm}</span>
        </div>
      </div>

      {/* Public Key */}
      <div className="cert-details-group">
        <h5 className="cert-details-group-title">Public Key</h5>
        <div className="cert-details-field">
          <span className="cert-details-label">Algorithm</span>
          <span className="cert-details-value">{cert.public_key.algorithm}</span>
        </div>
        {cert.public_key.key_size ? (
          <div className="cert-details-field">
            <span className="cert-details-label">Key Size</span>
            <span className="cert-details-value">{cert.public_key.key_size} bits</span>
          </div>
        ) : null}
        {cert.public_key.curve ? (
          <div className="cert-details-field">
            <span className="cert-details-label">Curve</span>
            <span className="cert-details-value">{cert.public_key.curve}</span>
          </div>
        ) : null}
        {cert.public_key.exponent ? (
          <div className="cert-details-field">
            <span className="cert-details-label">Exponent</span>
            <span className="cert-details-value">{cert.public_key.exponent}</span>
          </div>
        ) : null}
      </div>

      {/* Subject Alternative Names */}
      {hasSANs && (
        <div className="cert-details-group">
          <h5 className="cert-details-group-title">Subject Alternative Names</h5>
          {cert.subject_alt_names?.dns_names?.length ? (
            <div className="cert-details-field">
              <span className="cert-details-label">DNS Names</span>
              <div className="cert-details-list">
                {cert.subject_alt_names.dns_names.map((name, i) => (
                  <span key={i} className="cert-details-list-item">{name}</span>
                ))}
              </div>
            </div>
          ) : null}
          {cert.subject_alt_names?.ip_addresses?.length ? (
            <div className="cert-details-field">
              <span className="cert-details-label">IP Addresses</span>
              <div className="cert-details-list">
                {cert.subject_alt_names.ip_addresses.map((ip, i) => (
                  <span key={i} className="cert-details-list-item">{ip}</span>
                ))}
              </div>
            </div>
          ) : null}
          {cert.subject_alt_names?.email_addresses?.length ? (
            <div className="cert-details-field">
              <span className="cert-details-label">Email Addresses</span>
              <div className="cert-details-list">
                {cert.subject_alt_names.email_addresses.map((email, i) => (
                  <span key={i} className="cert-details-list-item">{email}</span>
                ))}
              </div>
            </div>
          ) : null}
          {cert.subject_alt_names?.uris?.length ? (
            <div className="cert-details-field">
              <span className="cert-details-label">URIs</span>
              <div className="cert-details-list">
                {cert.subject_alt_names.uris.map((uri, i) => (
                  <span key={i} className="cert-details-list-item">{uri}</span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Extensions */}
      {(keyUsageLabels.length > 0 || extKeyUsageLabels.length > 0 || cert.basic_constraints) && (
        <div className="cert-details-group">
          <h5 className="cert-details-group-title">Extensions</h5>
          {cert.basic_constraints && (
            <div className="cert-details-field">
              <span className="cert-details-label">Basic Constraints</span>
              <span className="cert-details-value">
                CA: {cert.basic_constraints.is_ca ? 'Yes' : 'No'}
                {cert.basic_constraints.is_ca && cert.basic_constraints.max_path_len !== undefined &&
                  `, Path Length: ${cert.basic_constraints.max_path_len}`}
              </span>
            </div>
          )}
          {keyUsageLabels.length > 0 && (
            <div className="cert-details-field">
              <span className="cert-details-label">Key Usage</span>
              <div className="cert-details-tags">
                {keyUsageLabels.map((label, i) => (
                  <span key={i} className="cert-details-tag">{label}</span>
                ))}
              </div>
            </div>
          )}
          {extKeyUsageLabels.length > 0 && (
            <div className="cert-details-field">
              <span className="cert-details-label">Extended Key Usage</span>
              <div className="cert-details-tags">
                {extKeyUsageLabels.map((label, i) => (
                  <span key={i} className="cert-details-tag">{label}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Key IDs */}
      {(cert.subject_key_id || cert.authority_key_id) && (
        <div className="cert-details-group">
          <h5 className="cert-details-group-title">Key Identifiers</h5>
          {cert.subject_key_id && (
            <div className="cert-details-field">
              <span className="cert-details-label">Subject Key ID</span>
              <span className="cert-details-value cert-details-mono cert-details-wrap">{cert.subject_key_id}</span>
            </div>
          )}
          {cert.authority_key_id && (
            <div className="cert-details-field">
              <span className="cert-details-label">Authority Key ID</span>
              <span className="cert-details-value cert-details-mono cert-details-wrap">{cert.authority_key_id}</span>
            </div>
          )}
        </div>
      )}

      {/* Fingerprints */}
      <div className="cert-details-group">
        <h5 className="cert-details-group-title">Fingerprints</h5>
        <div className="cert-details-field">
          <span className="cert-details-label">SHA-256</span>
          <div className="cert-details-fingerprint">
            <span className="cert-details-value cert-details-mono cert-details-wrap">{cert.fingerprints.sha256}</span>
            <button
              type="button"
              className="cert-details-copy-btn"
              onClick={() => handleCopy(cert.fingerprints.sha256, 'sha256')}
              title="Copy SHA-256 fingerprint"
              aria-label="Copy SHA-256 fingerprint"
            >
              {copiedField === 'sha256' ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
            </button>
          </div>
        </div>
        <div className="cert-details-field">
          <span className="cert-details-label">SHA-1</span>
          <div className="cert-details-fingerprint">
            <span className="cert-details-value cert-details-mono cert-details-wrap">{cert.fingerprints.sha1}</span>
            <button
              type="button"
              className="cert-details-copy-btn"
              onClick={() => handleCopy(cert.fingerprints.sha1, 'sha1')}
              title="Copy SHA-1 fingerprint"
              aria-label="Copy SHA-1 fingerprint"
            >
              {copiedField === 'sha1' ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
